// CSV download helper. Escapes commas, quotes, newlines per RFC 4180.
import JSZip from "jszip";

function escape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Build CSV string from rows.
 * @param {string[]} headers — column titles
 * @param {Array<Array|Object>} rows — array of arrays OR array of objects keyed by header
 */
export function toCSV(headers, rows) {
  const headerLine = headers.map(escape).join(",");
  const lines = rows.map((r) => {
    if (Array.isArray(r)) return r.map(escape).join(",");
    return headers.map((h) => escape(r[h])).join(",");
  });
  return [headerLine, ...lines].join("\n");
}

/** Trigger browser download of CSV. */
export function downloadCSV(filename, headers, rows) {
  const csv = toCSV(headers, rows);
  // BOM for Excel UTF-8 compatibility
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build a date-stamped filename. */
export function stampedName(prefix) {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `${prefix}_${stamp}.csv`;
}

/**
 * Bundle multiple CSVs into a single ZIP file and trigger download.
 * @param {string} zipName base filename (no extension)
 * @param {Array<{name: string, headers: string[], rows: any[]}>} sheets
 */
export async function downloadZip(zipName, sheets) {
  const zip = new JSZip();
  for (const s of sheets) {
    if (!s || !s.headers) continue;
    const csv = "\uFEFF" + toCSV(s.headers, s.rows || []);
    const fname = (s.name || "sheet").endsWith(".csv") ? s.name : `${s.name}.csv`;
    zip.file(fname, csv);
  }
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = zipName.endsWith(".zip") ? zipName : `${zipName}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Date-stamped zip filename. */
export function stampedZip(prefix) {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
  return `${prefix}_${stamp}.zip`;
}
